import { Button } from "@/components/ui/button";
import { AlertCircle, X } from "lucide-react";
import { ReactNode, useState } from "react";

interface FloatingBannerProps {
  message: string;
  buttonLabel: string;
  onButtonClick: () => void;
  onDismiss?: () => void;
}

interface FloatingBannerStackProps {
  children: ReactNode;
}

export function FloatingBannerStack({ children }: FloatingBannerStackProps) {
  return (
    <div className="fixed top-[60px] md:top-[80px] right-4 z-50 flex flex-col gap-2 max-w-[calc(100vw-2rem)]">
      {children}
    </div>
  );
}

export function FloatingBanner({
  message,
  buttonLabel,
  onButtonClick,
  onDismiss,
}: FloatingBannerProps) {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    onDismiss?.();
  };

  return (
    <div className="relative rounded-2xl bg-amber-50 border border-amber-300 shadow-lg">
      <button
        aria-label="Fechar aviso"
        onClick={handleDismiss}
        className="absolute top-2 right-2 p-1 rounded-full text-amber-600 hover:bg-amber-100 transition-colors"
      >
        <X className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
      </button>

      {/* Mobile: compacto sem ícone */}
      <div className="flex sm:hidden items-center gap-2 px-3 py-2.5 pr-8">
        <p className="text-xs font-medium text-amber-800 leading-snug flex-1">
          {message}
        </p>
        <Button
          size="sm"
          variant="default"
          className="bg-amber-600 hover:bg-amber-700 text-white hover:text-white shrink-0 text-xs h-7 px-2.5"
          onClick={onButtonClick}
        >
          {buttonLabel}
        </Button>
      </div>

      {/* Desktop: com ícone */}
      <div className="hidden sm:flex items-center gap-2 px-5 py-4 pr-12 max-w-md">
        <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
        <p className="text-sm font-medium text-amber-800 leading-snug flex-1">
          {message}
        </p>
        <Button
          size="sm"
          variant="default"
          className="bg-amber-600 hover:bg-amber-700 text-white hover:text-white shrink-0"
          onClick={onButtonClick}
        >
          {buttonLabel}
        </Button>
      </div>
    </div>
  );
}
